import React, { useMemo, useState } from "react";
import { useGetUsersQuery } from "state/api";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatDate,
} from "components/shared";

// Show the human label for the "user" role, which in this app means staff.
const roleLabel = (role) =>
  role === "user" ? "Staff" : role ? role.charAt(0).toUpperCase() + role.slice(1) : "—";

const Admin = () => {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  // Fetch ALL team members (admins, managers, staff) — not just role=admin.
  const { data, isFetching, error } = useGetUsersQuery(role ? { role } : {});

  const { rows } = useMemo(() => normalizeList(data), [data]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        render: (u) => u.name || "—",
      },
      {
        field: "email",
        headerName: "Email",
        render: (u) => u.email || "—",
      },
      {
        field: "role",
        headerName: "Role",
        render: (u) => (u.role ? <StatusChip status={u.role} label={roleLabel(u.role)} /> : "—"),
      },
      {
        field: "status",
        headerName: "Status",
        render: (u) => (u.status ? <StatusChip status={u.status} /> : "—"),
      },
      {
        field: "lastLogin",
        headerName: "Last Login",
        render: (u) => formatDate(u.lastLogin),
      },
      {
        field: "createdAt",
        headerName: "Joined",
        render: (u) => formatDate(u.createdAt),
      },
    ],
    []
  );

  return (
    <PageContainer title="Team" subtitle="Manage admins and team members">
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, email or role…"
        filters={[
          {
            label: "Role",
            value: role,
            onChange: setRole,
            options: [
              { label: "All Roles", value: "" },
              { label: "Admin", value: "admin" },
              { label: "Manager", value: "manager" },
              { label: "Staff", value: "user" },
            ],
          },
        ]}
      />
      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={isFetching}
        emptyText={
          error?.status === 403
            ? "You need an admin or manager account to view the team."
            : "No team members found"
        }
      />
    </PageContainer>
  );
};

export default Admin;
